import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { apiClient, ApiError } from '../services/apiClient';

const AuthContext = createContext({
  user: null,
  token: null,
  loading: true,
  login: async () => {},
  logout: () => {},
  refreshProfile: async () => {}
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const persistSession = (tokenValue, userValue) => {
    localStorage.setItem('authToken', tokenValue);
    localStorage.setItem('authUser', JSON.stringify(userValue));
    setToken(tokenValue);
    setUser(userValue);
  };

  const clearSession = useCallback(() => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    setToken(null);
    setUser(null);
  }, []);

  const login = useCallback(async ({ username, password }) => {
    try {
      const response = await apiClient.post('/auth/login', { username, password }, { credentials: 'include' });
      if (!response || !response.token || !response.user) {
        throw new Error('Ungültige Antwort vom Server.');
      }
      persistSession(response.token, response.user);
      return response.user;
    } catch (error) {
      if (error instanceof ApiError) {
        throw new Error(error.message || 'Login fehlgeschlagen.');
      }
      throw error;
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!token) {
      clearSession();
      return null;
    }

    try {
      const profile = await apiClient.get('/auth/me');
      if (!profile) {
        throw new Error('Profil konnte nicht geladen werden.');
      }
      localStorage.setItem('authUser', JSON.stringify(profile));
      setUser(profile);
      return profile;
    } catch (error) {
      console.warn('Profil konnte nicht aktualisiert werden, Session wird beendet.', error);
      clearSession();
      return null;
    }
  }, [token, clearSession]);

  const logout = useCallback(() => {
    clearSession();
  }, [clearSession]);

  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('authUser');

    if (storedToken) {
      setToken(storedToken);
    }

    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.warn('Konnte gespeicherten Nutzer nicht lesen, werde ihn zurücksetzen.', error);
        localStorage.removeItem('authUser');
      }
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const initialise = async () => {
      if (!token) {
        if (isMounted) {
          setLoading(false);
        }
        return;
      }

      try {
        await refreshProfile();
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initialise();

    return () => {
      isMounted = false;
    };
  }, [token, refreshProfile]);

  useEffect(() => {
    if (!token && loading) {
      setLoading(false);
    }
  }, [token, loading]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        logout,
        refreshProfile,
        isAuthenticated: Boolean(user && token)
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

